/**
 * Eckdaten in der Seitenspalte der Vorgangsseite („Salon“, Entscheidung E3, Antoine 26.09.2026):
 * nur Anzeige — Datum, Uhrzeit, Gäste, Ort, Aufbau — mit „Bearbeiten“, das den Drawer „Eckdaten“
 * öffnet (dort die vollbreiten Felder aus `AuftragEckdatenKarte`). Die schmale Spalte schnitt die
 * Eingabefelder vorher ab („18:3(“, „HH:r“).
 *
 * „Für Änderungen öffnen“ (Punkt 29 P1) und das Banner dazu stehen HIER statt im Drawer: der Weg
 * aus der Sperre soll sichtbar sein, ohne erst ein Fenster zu öffnen.
 */
import type { ReactNode } from "react";
import { Card, Icon, StatusPill } from "../components";
import { KleinFenster, StiftHinweis } from "./KleinFenster";
import { PILL_CLASSES, type PillTone, type StatusMeta } from "../format";
import { AenderungOffenBanner, FuerAenderungOeffnen } from "./FuerAenderungOeffnen";

interface EckdatenZeile {
  icon: string;
  ton: PillTone;
  label: string;
  wert: string;
  leer?: boolean;
  rechts?: StatusMeta | string | null;
  /** Direkt bearbeiten per Klein-Fenster (Antoine 28.09.2026). */
  bearbeiten?: (schliessen: () => void) => ReactNode;
}

/** Klein-Fenster je Zeile — gesetzt von der Vorgangsseite. */
export interface EckdatenBearbeiter {
  datum?: (schliessen: () => void) => ReactNode;
  uhrzeit?: (schliessen: () => void) => ReactNode;
  gaeste?: (schliessen: () => void) => ReactNode;
  ort?: (schliessen: () => void) => ReactNode;
}

/** „18:30“ / „18:30 – 22:00“ → „ab 18:30 Uhr“ / „18:30 – 22:00 Uhr“ (statt „– ? Uhr“). */
export function uhrzeitText(timeFrom: string | null | undefined, timeTo: string | null | undefined): string | null {
  if (!timeFrom) return null;
  const von = timeFrom.slice(0, 5);
  return timeTo ? `${von} – ${timeTo.slice(0, 5)} Uhr` : `ab ${von} Uhr`;
}

/** „Fr., 02.10.2026“ — kurz genug für die Seitenspalte. */
function kurzDatum(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
}

const AUFBAU: Record<string, string> = { indoor: "Innen", outdoor: "Außen", mixed: "Beides" };

export function EckdatenUebersicht({
  eventId,
  status,
  aktivitaeten,
  eventDate,
  tageBis,
  timeFrom,
  timeTo,
  guestCount,
  angebotGaeste,
  ort,
  ortFehlt,
  setupLocation,
  onBearbeiten,
  onGeoeffnet,
  bearbeiter = {},
}: {
  bearbeiter?: EckdatenBearbeiter;
  eventId: string;
  status: string;
  aktivitaeten: { action: string; createdAt: string }[];
  eventDate: string | null;
  tageBis: StatusMeta | null;
  timeFrom: string | null;
  timeTo: string | null;
  guestCount: number | null;
  /** Gästezahl der Ziel-Variante — für „wie im Angebot“ bzw. die Abweichung. */
  angebotGaeste: number | null;
  ort: string;
  ortFehlt: boolean;
  setupLocation: string | null;
  onBearbeiten: () => void;
  onGeoeffnet: () => void;
}) {
  const uhrzeit = uhrzeitText(timeFrom, timeTo);
  const gaesteRechts: StatusMeta | null =
    guestCount != null && angebotGaeste != null
      ? guestCount === angebotGaeste
        ? { label: "wie im Angebot", tone: "success" }
        : { label: `Angebot: ${angebotGaeste}`, tone: "warn" }
      : null;
  const zeilen: EckdatenZeile[] = [
    { icon: "calendar_today", ton: "primary", label: "Datum", wert: eventDate ? kurzDatum(eventDate) : "noch offen", leer: !eventDate, rechts: tageBis, bearbeiten: bearbeiter.datum },
    { icon: "schedule", ton: "primary", label: "Uhrzeit", wert: uhrzeit ?? "noch offen", leer: !uhrzeit, rechts: timeFrom && !timeTo ? "Ende offen" : null, bearbeiten: bearbeiter.uhrzeit },
    { icon: "group", ton: "info", label: "Gäste", wert: guestCount != null ? String(guestCount) : "noch offen", leer: guestCount == null, rechts: gaesteRechts, bearbeiten: bearbeiter.gaeste },
    { icon: "storefront", ton: ortFehlt ? "warn" : "success", label: "Ort", wert: ort, leer: ortFehlt, bearbeiten: bearbeiter.ort },
    ...(setupLocation ? [{ icon: "deck", ton: "success" as const, label: "Aufbau", wert: AUFBAU[setupLocation] ?? setupLocation }] : []),
  ];
  return (
    <Card className="p-5" data-testid="eckdaten-uebersicht">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="text-[15px] font-bold text-on-background dark:text-inverse-on-surface">Eckdaten</h3>
        <div className="flex items-center gap-3">
          <FuerAenderungOeffnen eventId={eventId} status={status} onGeoeffnet={onGeoeffnet} />
          <button
            type="button"
            data-testid="eckdaten-bearbeiten"
            onClick={onBearbeiten}
            className="inline-flex items-center gap-1 text-[12.5px] font-bold text-primary hover:underline"
          >
            <Icon name="edit" className="text-[16px]" /> Bearbeiten
          </button>
        </div>
      </div>
      <AenderungOffenBanner status={status} aktivitaeten={aktivitaeten} eventId={eventId} />
      <ul>
        {zeilen.map((z) => {
          const inhalt = (
            <>
              <span className={`grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[9px] ${PILL_CLASSES[z.ton]}`}>
                <Icon name={z.icon} className="text-[17px]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[11.5px] font-semibold text-secondary-fixed-dim">{z.label}</span>
                <span className={`block truncate ${z.leer ? "font-medium text-secondary-fixed-dim" : "font-bold text-on-background dark:text-inverse-on-surface"}`}>
                  {z.wert}
                </span>
              </span>
              {z.rechts &&
                (typeof z.rechts === "string" ? (
                  <span className="shrink-0 text-[11.5px] text-secondary-fixed-dim">{z.rechts}</span>
                ) : (
                  <span className="shrink-0">
                    <StatusPill meta={z.rechts} />
                  </span>
                ))}
            </>
          );
          const zeile = "flex w-full items-center gap-3 py-2.5 text-left";
          return (
            <li key={z.label} className="border-t border-surface-container first:border-t-0 dark:border-white/[0.05]">
              {z.bearbeiten ? (
                <KleinFenster
                  testId={`eckdaten-${z.label.toLowerCase()}`}
                  ausloeser={({ offen, umschalten }) => (
                    <button
                      type="button"
                      onClick={umschalten}
                      aria-expanded={offen}
                      title={`${z.label} bearbeiten`}
                      data-testid={`eckdaten-${z.label.toLowerCase()}`}
                      className={`${zeile} group -mx-2 rounded-lg px-2 hover:bg-surface-container-low dark:hover:bg-white/[0.04] ${offen ? "bg-surface-container-low dark:bg-white/[0.04]" : ""}`}
                    >
                      {inhalt}
                      <StiftHinweis />
                    </button>
                  )}
                >
                  {z.bearbeiten}
                </KleinFenster>
              ) : (
                <div className={zeile}>{inhalt}</div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
